import React, { useCallback, useEffect, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import {
  loadAppMessages,
  markAppMessageRead,
} from "../storage/appMessagesStorage.js";
import { useLanguage } from "../i18n/LanguageContext.js";

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

/**
 * In-app inbox — check-ins from notifications and follow-ups.
 *
 * @param {{
 *   onBack: () => void,
 *   onOpenFailDetails: (dateKey: string) => void,
 *   onRefreshBadge: () => void,
 * }} props
 */
export function MessagesScreen({ onBack, onOpenFailDetails, onRefreshBadge }) {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const refresh = useCallback(async () => {
    const list = await loadAppMessages();
    setMessages(list);
    onRefreshBadge?.();
  }, [onRefreshBadge]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openMessage = async (msg) => {
    setExpandedId(msg.id);
    if (!msg.read) {
      await markAppMessageRead(msg.id);
      await refresh();
    }
  };

  const startFailDetails = async (msg) => {
    await markAppMessageRead(msg.id);
    await refresh();
    onOpenFailDetails(msg.dateKey);
  };

  return (
    <View style={styles.flex}>
      <View style={styles.header}>
        <Text style={styles.title}>{t("notif_title")}</Text>
        <Pressable onPress={onBack}>
          <Text style={styles.back}>{t("generic_back")}</Text>
        </Pressable>
      </View>

      <Text style={styles.subtitle}>
        {t("notif_subtitle")}
      </Text>

      <ScrollView style={styles.list} contentContainerStyle={{ paddingBottom: 24 }}>
        {messages.length === 0 ? (
          <Text style={styles.empty}>{t("notif_empty")}</Text>
        ) : (
          messages.map((msg) => {
            const expanded = expandedId === msg.id;
            const needsFail = msg.type === "fail_notification" && msg.needsFailDetails && !msg.failDetailsCompleted;
            return (
              <Pressable key={msg.id} style={[styles.card, needsFail && styles.cardUrgent]} onPress={() => void openMessage(msg)}>
                <View style={styles.cardRow}>
                  <Text style={styles.cardTitle}>{msg.title}</Text>
                  {!msg.read || needsFail ? <View style={styles.dot} /> : null}
                </View>
                <Text style={styles.cardWhen}>{formatWhen(msg.createdAt)} · {msg.dateKey}</Text>
                <Text style={styles.cardBody} numberOfLines={expanded ? undefined : 3}>
                  {msg.body}
                </Text>

                {expanded && needsFail ? (
                  <View style={styles.promptBox}>
                    <Text style={styles.promptTitle}>{t("notif_fail_prompt_title")}</Text>
                    <Text style={styles.promptBody}>{t("notif_fail_prompt_body")}</Text>
                    <Pressable style={styles.primaryBtn} onPress={() => void startFailDetails(msg)}>
                      <Text style={styles.primaryBtnText}>{t("notif_add_fail_details")}</Text>
                    </Pressable>
                  </View>
                ) : null}

                {expanded && msg.type === "success_notification" ? (
                  <Text style={styles.successNote}>{t("notif_logged")}</Text>
                ) : null}
              </Pressable>
            );
          })
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  title: { fontSize: 24, fontWeight: "800", color: "#111" },
  back: { color: "#2563EB", fontWeight: "700", fontSize: 16 },
  subtitle: { fontSize: 13, color: "#6B7280", lineHeight: 19, marginBottom: 14 },
  list: { flex: 1, maxHeight: 520 },
  empty: { color: "#9CA3AF", fontSize: 14, lineHeight: 20 },
  card: {
    backgroundColor: "#F8FAFC",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
  },
  cardUrgent: { borderColor: "#FCA5A5", backgroundColor: "#FEF2F2" },
  cardRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  cardTitle: { fontSize: 16, fontWeight: "800", color: "#111" },
  dot: { width: 10, height: 10, borderRadius: 5, backgroundColor: "#EF4444" },
  cardWhen: { fontSize: 11, color: "#9CA3AF", marginTop: 4, marginBottom: 8 },
  cardBody: { fontSize: 14, color: "#374151", lineHeight: 20 },
  promptBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 10,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#FECACA",
  },
  promptTitle: { fontSize: 15, fontWeight: "800", color: "#B91C1C", marginBottom: 6 },
  promptBody: { fontSize: 13, color: "#4B5563", lineHeight: 18, marginBottom: 10 },
  primaryBtn: {
    backgroundColor: "#E74C3C",
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
  },
  primaryBtnText: { color: "#fff", fontWeight: "800" },
  successNote: { marginTop: 10, fontSize: 13, color: "#047857", fontWeight: "600" },
});
